import { getPriceForPart } from "./getPriceForPart";
import { countTotalRivets, isDuplicateSupplyPart } from "./rivets";
import {
  type MaterialInfo,
  type MaterialPosition,
  MaterialType,
  type PartLike,
  type PriceCalcOptions,
  type PriceCalculation,
  type PriceCalculationIssue,
  type PriceCalculationIssueGrouped,
  type PriceData,
  type PriceParameters,
  PriceUnit,
} from "./types";

function aggregatePriceIssues(
  raw: PriceCalculationIssue[],
): PriceCalculationIssueGrouped[] {
  const map = new Map<string, PriceCalculationIssueGrouped>();
  for (const issue of raw) {
    const key = `${issue.kind}\0${issue.code ?? ""}\0${issue.detail}`;
    const prev = map.get(key);
    if (prev) prev.count += 1;
    else
      map.set(key, {
        count: 1,
        kind: issue.kind,
        code: issue.code,
        detail: issue.detail,
      });
  }
  return [...map.values()].sort(
    (a, b) => b.count - a.count || a.detail.localeCompare(b.detail, "de"),
  );
}

function panelKindLabelForIssue(props: PartLike): string {
  if (props.type === "TrayPanel") return "Tableau";
  if (props.type !== "Panel") return "Platte";
  const panel = props.panelType;
  if (typeof panel === "number") {
    return (
      ["Außenplatte", "Tableau", "Rollbrett", "Unterlage", "Schienen"][panel] ??
      "Platte"
    );
  }
  return "Platte";
}

/** Bezeichnung für fehlenden DB-Eintrag (gruppierbar pro Farbe/Typ). */
function detailForMissingDbEntry(props: PartLike, info: MaterialInfo): string {
  const color = props.color as
    | { name?: string; description?: string }
    | undefined;
  if (props.type === "Panel" || props.type === "TrayPanel") {
    const kind = panelKindLabelForIssue(props);
    const colorName = color?.name ?? color?.description ?? "";
    if (colorName) return `${kind} ${colorName}`;
    return `${kind} (${info.code})`;
  }
  return info.description || info.code;
}

function partLabelForMissingCode(props: PartLike): string {
  const t = props.type ?? "?";
  const name =
    props.partSelection?.name ??
    props.partSelection?.description ??
    props.color?.name ??
    props.material?.name;
  const strength =
    typeof props.strength === "number" ? `, ${props.strength} mm` : "";
  if (props.type === "Panel" || props.type === "TrayPanel") {
    const panel = props.panelType;
    const panelHint =
      typeof panel === "number"
        ? (["Außenplatte", "Tableau", "Rollbrett", "Unterlage", "Schienen"][
            panel
          ] ?? `Panel-Typ ${panel}`)
        : "";
    return name
      ? `${t} (${panelHint || "Platte"}): ${name}${strength}`
      : `${t} (${panelHint || "Platte"})${strength}`;
  }
  return name ? `${t}: ${name}` : t;
}

const DEFAULT_RIVET_ARTICLE_CODE = "12345";
const DEFAULT_RIVET_PRICE_DIVISOR = 1000;
const DEFAULT_VAT_RATE = 0.19;

function normalizePartList(
  partList: Iterable<PartLike> | Map<string, PartLike>,
): PartLike[] {
  if (partList instanceof Map) {
    return Array.from(partList.values());
  }

  if (Array.isArray(partList)) {
    return partList;
  }

  if (partList && typeof partList === "object") {
    const values = (partList as { values?: () => Iterable<PartLike> }).values;
    if (typeof values === "function") {
      return Array.from(values.call(partList));
    }
  }

  if (
    partList &&
    typeof (partList as Iterable<PartLike>)[Symbol.iterator] === "function"
  ) {
    return Array.from(partList as Iterable<PartLike>);
  }

  return [];
}

function sumBy<T>(items: T[], selector: (item: T) => number): number {
  return items.reduce((acc, item) => acc + selector(item), 0);
}

function handleMissingPrice(
  code: string,
  behavior: PriceCalcOptions["onMissingPrice"],
): void {
  const message = `No price entry for code ${code}`;
  switch (behavior) {
    case "throw":
      throw new Error(message);
    case "ignore":
      return;
    default:
      console.error(message);
  }
}

export function calculatePrice(
  partList: Iterable<PartLike> | Map<string, PartLike>,
  priceData: PriceData,
  parameters: PriceParameters,
  options: PriceCalcOptions = {},
): PriceCalculation | null {
  if (!priceData || Object.keys(priceData).length === 0) {
    return null;
  }

  const parts = normalizePartList(partList);
  const material: MaterialPosition[] = [];
  const priceIssues: PriceCalculationIssue[] = [];
  const { onMissingPrice = "warn" } = options;

  const rivetArticleCode =
    options.rivetArticleCode ?? DEFAULT_RIVET_ARTICLE_CODE;
  const rivetPriceDivisor =
    options.rivetPriceDivisor ?? DEFAULT_RIVET_PRICE_DIVISOR;
  const vatRate = options.vatRate ?? DEFAULT_VAT_RATE;

  const rivets = countTotalRivets(parts, priceData);

  const rivetEntry = priceData[rivetArticleCode];
  if (!rivetEntry) {
    handleMissingPrice(rivetArticleCode, onMissingPrice);
    priceIssues.push({
      kind: "missing_db_entry",
      code: rivetArticleCode,
      detail: "Nieten",
    });
  }

  const rivetPrice = rivetEntry
    ? rivetEntry.PricePurchase / rivetPriceDivisor
    : 0;

  for (const part of parts) {
    const props = part.properties ?? part;
    if (props.dontShowInPlan) continue;
    if (isDuplicateSupplyPart(props)) continue;

    const info = getPriceForPart(props);
    if (!info) {
      if (props.type !== "Volume" && props.type !== "Measurements") {
        priceIssues.push({
          kind: "missing_material_code",
          detail: partLabelForMissingCode(props),
        });
      }
      continue;
    }

    const entry = priceData[info.code];
    if (!entry) {
      handleMissingPrice(info.code, onMissingPrice);
      priceIssues.push({
        kind: "missing_db_entry",
        code: info.code,
        detail: detailForMissingDbEntry(props, info),
      });
      continue;
    }

    const work = entry.WorkingTimeMinutes
      ? entry.WorkingTimeMinutes * info.amount
      : 0;

    material.push({
      ...info,
      price: entry.PricePurchase,
      total: entry.PricePurchase * info.amount,
      work,
    });
  }

  material.push({
    amount: rivets,
    code: rivetArticleCode,
    description: "Nieten",
    price: rivetPrice,
    total: rivets * rivetPrice,
    type: MaterialType.Other,
    unit: PriceUnit.Piece,
    work: 0,
  });

  const sumMaterial = sumBy(material, (item) => item.total);
  const wastedPanel =
    sumBy(
      material.filter((item) => item.type === MaterialType.Panel),
      (item) => item.total,
    ) * parameters.pPanel;
  const wastedAluminium =
    sumBy(
      material.filter((item) => item.type === MaterialType.Alu),
      (item) => item.total,
    ) * parameters.pAlu;
  const procurementCost =
    (sumMaterial + wastedAluminium + wastedPanel) * parameters.pNKMat;
  const materialTotal =
    procurementCost + sumMaterial + wastedAluminium + wastedPanel;

  const amountMarginMaterial = materialTotal * parameters.pMarginMaterial;
  const workTimeInHours =
    (sumBy(material, (item) => item.work) / 60) * (1 + parameters.pNebenzeit);
  const additionalCost =
    workTimeInHours * parameters.workCost + parameters.otherCost;
  const intermediate1 = materialTotal + additionalCost + amountMarginMaterial;
  const amountMarginTotal = intermediate1 * parameters.pMarginTotal;
  const extraTime = parameters.pNebenzeit * workTimeInHours;
  const priceNetto = intermediate1 * (1 + parameters.pMarginTotal);
  const priceBrutto = priceNetto * (1 + vatRate);

  return {
    additionalCost,
    amountMarginMaterial,
    amountMarginTotal,
    extraTime,
    material,
    materialTotal,
    priceBrutto,
    priceNetto,
    priceIssues: aggregatePriceIssues(priceIssues),
    procurementCost,
    sumMaterial,
    wastedAluminium,
    wastedPanel,
    workTime: workTimeInHours,
  };
}
